/**
 * Página principal de gestión de envíos
 */
import React, { useState, useEffect, useContext } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Card, Typography, Divider, useMediaQuery, useTheme } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import AssignmentIcon from '@mui/icons-material/Assignment';

import {
    DATA_GRID_SX,
    DATA_GRID_SLOT_PROPS,
    DATA_GRID_LOCALE_TEXT,
} from '../../../components/ui/dataGridStyles';
import { StateContext } from '../../../components/context/StateProvider';
import EnviosService from '../api/envios.api';
import Toasts from '../../../components/notificacion/Toasts';
import { FiltrosEnvios, ModalComentario, ModalConfirmarEstado, EnviosMobileList } from '../components';
import {
    generarColumnas,
    filtrarPorAgencia,
    obtenerAgenciasDisponibles,
    calcularTotalBultos,
    contarExpediciones,
    generarIdFila,
    esClienteFinal,
} from '../helpers';
import {
    DIAS_ATRAS_DEFAULT,
    ENVIO_ESTADO,
    CLASE_FILA_CLIENTE_FINAL,
    FONDO_CLIENTE_FINAL,
    FONDO_CLIENTE_FINAL_HOVER,
} from '../constants';

// DECISION: 200 filas por página (no las 10 estándar) porque esta pantalla se revisa
// la jornada entera de un vistazo
const ENVIOS_INITIAL_STATE = { pagination: { paginationModel: { pageSize: 200 } } };
const ENVIOS_PAGE_SIZE_OPTIONS = [100, 200, 500];

// Estilo común + filas de cliente final (después del hover genérico para que la fila
// no pierda su color)
const ENVIOS_GRID_SX = {
    ...DATA_GRID_SX,
    [`& .${CLASE_FILA_CLIENTE_FINAL}`]: {
        backgroundColor: FONDO_CLIENTE_FINAL,
        '&:hover': { backgroundColor: FONDO_CLIENTE_FINAL_HOVER },
    },
};

const ListaEnvios = () => {
    const { state } = useContext(StateContext);
    const { updateState } = useContext(StateContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

    // Estado de datos
    const [envios, setEnvios] = useState([]);
    const [loading, setLoading] = useState(true);

    // Estados de filtros
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [serieNumero, setSerieNumero] = useState('');
    const [agencia, setAgencia] = useState('');

    // Estados de modales
    const [modalComentario, setModalComentario] = useState({ open: false, envio: null });
    const [modalConfirmEstado, setModalConfirmEstado] = useState({ open: false, envio: null });
    const [loadingModal, setLoadingModal] = useState(false);

    // Datos derivados
    const agenciasDisponibles = obtenerAgenciasDisponibles(envios);
    const enviosFiltrados = filtrarPorAgencia(envios, agencia);
    const totalBultos = calcularTotalBultos(enviosFiltrados);
    const numExpediciones = contarExpediciones(enviosFiltrados);

    useEffect(() => {
        inicializarFechas();
    }, []);

    const inicializarFechas = () => {
        const hoy = new Date();
        const fin = hoy.toISOString().slice(0, 10);

        const inicioDate = new Date();
        inicioDate.setDate(hoy.getDate() - DIAS_ATRAS_DEFAULT);
        const inicio = inicioDate.toISOString().slice(0, 10);

        setFechaInicio(inicio);
        setFechaFin(fin);

        cargarEnvios(inicio, fin, serieNumero, true);
    };

    const cargarEnvios = async (fi, ff, sn, sp) => {
        setLoading(true);
        try {
            const data = await EnviosService.list({
                fechaInicio: fi,
                fechaFin: ff,
                serieNumero: sn?.trim() || undefined,
                soloPendientes: sp,
                ejercicio: state.year,
            });
            setEnvios(data);
        } catch (error) {
            const toast = Toasts.generateToast(
                error.message || 'Error al cargar envíos',
                'error'
            );
            updateState({ toasts: [...state.toasts, toast] });
        } finally {
            setLoading(false);
        }
    };

    const handleBuscar = () => {
        cargarEnvios(fechaInicio, fechaFin, serieNumero, false);
    };

    const handleBuscarPendientes = () => {
        cargarEnvios(fechaInicio, fechaFin, serieNumero, true);
    };

    const handleLimpiar = () => {
        setAgencia('');
        setSerieNumero('');
    };

    // Handlers de modales
    const handleOpenComentario = (envio) => {
        setModalComentario({ open: true, envio });
    };

    const handleCloseComentario = () => {
        setModalComentario({ open: false, envio: null });
    };

    const handleSaveComentario = async (comentario) => {
        if (!modalComentario.envio) return;

        setLoadingModal(true);
        try {
            await EnviosService.update({
                serie: modalComentario.envio.serie,
                albaran: modalComentario.envio.albaran,
                comentario,
                ejercicio: state.year,
            });

            // Actualizar estado local
            setEnvios((prev) =>
                prev.map((e) =>
                    e.serie === modalComentario.envio.serie &&
                    e.albaran === modalComentario.envio.albaran &&
                    String(e.linea ?? '') === String(modalComentario.envio.linea ?? '')
                        ? { ...e, tracking_comentario: comentario }
                        : e
                )
            );

            handleCloseComentario();

            const toast = Toasts.generateToast('Comentario guardado correctamente', 'success');
            updateState({ toasts: [...state.toasts, toast] });
        } catch (error) {
            const toast = Toasts.generateToast(
                error.message || 'No se pudo guardar el comentario',
                'error'
            );
            updateState({ toasts: [...state.toasts, toast] });
        } finally {
            setLoadingModal(false);
        }
    };

    const handleOpenConfirmEstado = (envio) => {
        setModalConfirmEstado({ open: true, envio });
    };

    const handleCloseConfirmEstado = () => {
        setModalConfirmEstado({ open: false, envio: null });
    };

    const handleConfirmCambioEstado = async () => {
        if (!modalConfirmEstado.envio) return;

        setLoadingModal(true);
        try {
            await EnviosService.update({
                serie: modalConfirmEstado.envio.serie,
                albaran: modalConfirmEstado.envio.albaran,
                estado: ENVIO_ESTADO.ENTREGADO,
                ejercicio: state.year,
            });

            // Actualizar estado local
            setEnvios((prev) =>
                prev.map((e) =>
                    e.serie === modalConfirmEstado.envio.serie &&
                    e.albaran === modalConfirmEstado.envio.albaran &&
                    String(e.linea ?? '') === String(modalConfirmEstado.envio.linea ?? '')
                        ? { ...e, tracking_estado: ENVIO_ESTADO.ENTREGADO }
                        : e
                )
            );

            handleCloseConfirmEstado();

            const toast = Toasts.generateToast('Estado actualizado correctamente', 'success');
            updateState({ toasts: [...state.toasts, toast] });
        } catch (error) {
            const toast = Toasts.generateToast(
                error.message || 'No se pudo forzar el estado',
                'error'
            );
            updateState({ toasts: [...state.toasts, toast] });
        } finally {
            setLoadingModal(false);
        }
    };

    const columns = generarColumnas(
        handleOpenConfirmEstado,
        handleOpenComentario,
        state.year
    );

    return (
        <Box id="lista-envios" sx={{ width: '100%' }}>
            <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: 2 }}>
                {/* Header */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Box>
                        <Typography
                            variant={isMobile ? 'h6' : 'h5'}
                            sx={{
                                fontWeight: 700,
                                color: '#212529',
                                mb: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <LocalShippingIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                            Envíos
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6c757d' }}>
                            Consulta y gestiona el estado de los envíos
                        </Typography>
                    </Box>

                    {/* Estadísticas */}
                    {!loading && envios.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: 'repeat(2, 1fr)',
                                        sm: 'repeat(2, 1fr)',
                                    },
                                    gap: 1.5,
                                }}
                            >
                                {/* Total bultos */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                        gap: { xs: 0.5, sm: 0.75 },
                                        px: { xs: 1.5, sm: 2 },
                                        py: { xs: 1.5, sm: 1.25 },
                                        borderRadius: 1,
                                        backgroundColor: '#e7f1ff',
                                        border: '2px solid transparent',
                                    }}
                                >
                                    <InventoryIcon
                                        sx={{
                                            fontSize: { xs: 24, sm: 22 },
                                            color: '#0d6efd',
                                        }}
                                    />
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 12, sm: 14 },
                                                fontWeight: 600,
                                                color: '#495057',
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            Total bultos
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 18, sm: 16 },
                                                fontWeight: 700,
                                                color: '#0d6efd',
                                                lineHeight: 1,
                                            }}
                                        >
                                            {totalBultos}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Total expediciones */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                        gap: { xs: 0.5, sm: 0.75 },
                                        px: { xs: 1.5, sm: 2 },
                                        py: { xs: 1.5, sm: 1.25 },
                                        borderRadius: 1,
                                        backgroundColor: '#e7f1ff',
                                        border: '2px solid transparent',
                                    }}
                                >
                                    <AssignmentIcon
                                        sx={{
                                            fontSize: { xs: 24, sm: 22 },
                                            color: '#0d6efd',
                                        }}
                                    />
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 12, sm: 14 },
                                                fontWeight: 600,
                                                color: '#495057',
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            Total expediciones
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 18, sm: 16 },
                                                fontWeight: 700,
                                                color: '#0d6efd',
                                                lineHeight: 1,
                                            }}
                                        >
                                            {numExpediciones}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </>
                    )}

                    {/* Filtros */}
                    <Box sx={{ mt: 3 }}>
                        <FiltrosEnvios
                            fechaInicio={fechaInicio}
                            setFechaInicio={setFechaInicio}
                            fechaFin={fechaFin}
                            setFechaFin={setFechaFin}
                            serieNumero={serieNumero}
                            setSerieNumero={setSerieNumero}
                            agencia={agencia}
                            setAgencia={setAgencia}
                            agenciasDisponibles={agenciasDisponibles}
                            onBuscar={handleBuscar}
                            onBuscarPendientes={handleBuscarPendientes}
                            onLimpiar={handleLimpiar}
                            loading={loading}
                            totalResultados={enviosFiltrados.length}
                        />
                    </Box>
                </Box>

                {/* DataGrid / Mobile List */}
                <Box sx={{ px: isMobile ? 2 : 3, pb: 3 }}>
                    {!isMobile ? (
                        <DataGrid
                            rows={enviosFiltrados}
                            columns={columns}
                            getRowId={generarIdFila}
                            localeText={DATA_GRID_LOCALE_TEXT}
                            autoHeight
                            loading={loading}
                            initialState={ENVIOS_INITIAL_STATE}
                            pageSizeOptions={ENVIOS_PAGE_SIZE_OPTIONS}
                            disableRowSelectionOnClick
                            getRowClassName={(params) =>
                                esClienteFinal(params.row) ? CLASE_FILA_CLIENTE_FINAL : ''}
                            slotProps={DATA_GRID_SLOT_PROPS}
                            sx={ENVIOS_GRID_SX}
                        />
                    ) : (
                        <EnviosMobileList
                            rows={enviosFiltrados}
                            loading={loading}
                            onOpenComentario={handleOpenComentario}
                            onOpenConfirmEstado={handleOpenConfirmEstado}
                            ejercicio={state.year}
                        />
                    )}
                </Box>
            </Card>

            {/* Modales */}
            <ModalComentario
                open={modalComentario.open}
                onClose={handleCloseComentario}
                envio={modalComentario.envio}
                onSave={handleSaveComentario}
                loading={loadingModal}
            />

            <ModalConfirmarEstado
                open={modalConfirmEstado.open}
                onClose={handleCloseConfirmEstado}
                envio={modalConfirmEstado.envio}
                onConfirm={handleConfirmCambioEstado}
                loading={loadingModal}
            />
        </Box>
    );
};

export default ListaEnvios;
